import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { deriveOrgName } from '@ovrsee/shared'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { Button, Input } from '../../ui/index.js'
import { usePageTitle } from '../../lib/page-title.js'
import { AuthLayout } from './AuthLayout.js'
import { GoogleButton } from './GoogleButton.js'
import { MIN_PASSWORD_LENGTH, PasswordStrength } from './PasswordStrength.js'
import { FieldError, FormError } from './FormError.js'

export function SignUp() {
  usePageTitle('Sign up')
  const { api, signIn } = useAuth()
  const navigate = useNavigate()
  const [organisationName, setOrganisationName] = useState('')
  const [orgNameTouched, setOrgNameTouched] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Derive only until the user edits the field themselves: three existing tests type the
  // workspace name first, and a real user's correction must never be silently overwritten.
  function updateEmail(next: string) {
    setEmail(next)
    if (!orgNameTouched) setOrganisationName(deriveOrgName(next, name))
  }

  function updateName(next: string) {
    setName(next)
    if (!orgNameTouched) setOrganisationName(deriveOrgName(email, next))
  }

  function updateOrganisationName(next: string) {
    setOrgNameTouched(true)
    setOrganisationName(next)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)

    const next: Record<string, string> = {}
    if (name.trim() === '') next['name'] = 'Enter your name'
    if (email.trim() === '') next['email'] = 'Enter your email address'
    if (organisationName.trim() === '') next['organisationName'] = 'Enter a workspace name'
    if (password.length < MIN_PASSWORD_LENGTH) {
      next['password'] = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return

    setBusy(true)
    try {
      const result = await api.post<{ accessToken: string; refreshToken: string }>('/api/auth/register-organisation', {
        organisationName: organisationName.trim(),
        name: name.trim(),
        email: email.trim(),
        password,
      })
      await signIn(result)
      navigate('/', { replace: true })
    } catch (error) {
      if (error instanceof ApiError && error.isValidation && error.details !== undefined) {
        setErrors(error.details)
      } else if (error instanceof ApiError) {
        setFormError(error.message)
      } else {
        setFormError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Create your workspace"
      subtitle="Free for 14 days. No credit card required."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <GoogleButton intent="signup" />
      <form onSubmit={submit} noValidate>
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="name">
            Full name
          </label>
          <Input id="name" name="name" type="text" value={name} onChange={updateName} />
          <FieldError message={errors['name']} />
        </div>

        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="email">
            Work email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={updateEmail}
          />
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>
            We'll set up your team workspace from your company domain.
          </div>
          <FieldError message={errors['email']} />
        </div>

        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="organisationName">
            Workspace name
          </label>
          <Input
            id="organisationName"
            name="organisationName"
            type="text"
            value={organisationName}
            onChange={updateOrganisationName}
          />
          <FieldError message={errors['organisationName']} />
        </div>

        <div className="field" style={{ marginBottom: 'var(--o-space-5)' }}>
          <label className="field-label" htmlFor="password">
            Password
          </label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
            value={password}
            onChange={setPassword}
          />
          <PasswordStrength password={password} />
          <FieldError message={errors['password']} />
        </div>

        <label
          htmlFor="agree"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '4px 0 20px', fontSize: 13, cursor: 'pointer' }}
        >
          <input
            id="agree"
            name="agree"
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            style={{ accentColor: 'var(--brand)', width: 15, height: 15, marginTop: 2 }}
          />
          I agree to the Terms of Service and Privacy Policy
        </label>

        <FormError message={formError} />

        <Button type="submit" kind="primary" disabled={busy || !agreed} style={{ width: '100%', opacity: agreed ? 1 : 0.55 }}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  )
}
